import React, { useEffect, useRef, useState } from "react";

export default function LazyVideo({
  src,
  poster,
  className,
  autoPlay,
  muted,
  loop,
  controls,
  playsInline,
  // preload default is none to avoid fetching until visible
  preload = "none",
  // priority: when true, load immediately (useful for above-the-fold videos)
  priority = false,
  children,
  onLoadCallback,
  ...rest
}) {
  const ref = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) return; // already loading
    if (typeof window === "undefined") {
      // server-side render: defer to client
      return;
    }

    // If caller flagged this video as priority, start loading immediately
    if (priority) {
      setShouldLoad(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setShouldLoad(true);
              io.disconnect();
            }
          });
        },
        { rootMargin: "200px" } // start loading just before it's visible
      );
      io.observe(el);
      return () => io.disconnect();
    }

    // Fallback: load immediately
    setShouldLoad(true);
  }, [shouldLoad, priority]);

  useEffect(() => {
    if (!shouldLoad) return;
    const v = ref.current;
    if (!v) return;
    // if sources were added dynamically, call load()
    try {
      v.load?.();
      if (autoPlay && muted) {
        // attempt autoplay when muted (browsers allow muted autoplay)
        const p = v.play?.();
        if (p && typeof p.then === "function") p.catch(() => {});
      }
      onLoadCallback?.();
    } catch (e) {
      // ignore
    }
  }, [shouldLoad, autoPlay, muted, onLoadCallback]);

  // If priority is requested and caller didn't override preload, use 'auto' so the browser fetches more than metadata
  const effectivePreload = priority && preload === "none" ? "auto" : preload;

  return (
    <video
      ref={ref}
      className={className}
      poster={poster}
      preload={effectivePreload}
      autoPlay={shouldLoad && !!autoPlay}
      muted={muted}
      loop={loop}
      controls={controls}
      playsInline={playsInline}
      {...rest}
    >
      {shouldLoad ? (
        // if children (<source />) are provided, render them; otherwise, render a single source with `src` prop
        children ? (
          children
        ) : src ? (
          <source src={src} type="video/mp4" />
        ) : null
      ) : null}
      {/* keep fallback text in case it's present in children or for older browsers */}
    </video>
  );
}
